import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

// Replaces native alert(). A native dialog blocks the renderer until a human
// dismisses it, which stops every automated run dead — Playwright, the browser
// tooling we verify staging with, anything. These messages never block.

export type MessageSeverity = 'info' | 'success' | 'warning' | 'error';

export interface AppMessage {
  id: number;
  text: string;
  severity: MessageSeverity;
}

interface MessageContextValue {
  messages: AppMessage[];
  showMessage: (text: string, severity?: MessageSeverity) => void;
  dismissMessage: (id: number) => void;
}

const MessageContext = createContext<MessageContextValue | null>(null);

export function useMessages(): MessageContextValue {
  const ctx = useContext(MessageContext);
  // Fail fast: a message that silently goes nowhere hides the very errors it
  // was meant to report.
  if (!ctx) throw new Error('useMessages must be used within a MessageProvider');
  return ctx;
}

// Severity is carried by LUMINANCE as well as hue — the palette must stay legible
// to a red-green colourblind reader, so the error tone is dark and the success
// tone is darker still rather than relying on red-vs-green.
const TONE: Record<MessageSeverity, { background: string; border: string }> = {
  info: { background: '#1f3a5f', border: '#4a7ab8' },
  success: { background: '#14532d', border: '#2f8f52' },
  warning: { background: '#5c4209', border: '#c99a1e' },
  error: { background: '#5e1414', border: '#c0392b' },
};

let nextId = 0;

export function MessageProvider({ children }: { children: React.ReactNode }) {
  const [messages, setMessages] = useState<AppMessage[]>([]);

  const dismissMessage = useCallback((id: number) => {
    setMessages((current) => current.filter((m) => m.id !== id));
  }, []);

  const showMessage = useCallback((text: string, severity: MessageSeverity = 'info') => {
    setMessages((current) => [...current, { id: (nextId += 1), text, severity }]);
  }, []);

  const value = useMemo(
    () => ({ messages, showMessage, dismissMessage }),
    [messages, showMessage, dismissMessage],
  );

  return (
    <MessageContext.Provider value={value}>
      {children}
      <div className="app-messages" style={CONTAINER_STYLE}>
        {messages.map((message) => (
          <div
            key={message.id}
            // An error interrupts; anything else waits for a pause in speech.
            role={message.severity === 'error' ? 'alert' : 'status'}
            style={{ ...MESSAGE_STYLE, ...TONE[message.severity] }}
          >
            <span>{message.text}</span>
            <button
              type="button"
              aria-label={`Dismiss message: ${message.text}`}
              onClick={() => dismissMessage(message.id)}
              style={DISMISS_STYLE}
            >
              ×
            </button>
          </div>
        ))}
      </div>
    </MessageContext.Provider>
  );
}

const CONTAINER_STYLE: React.CSSProperties = {
  position: 'fixed',
  bottom: 16,
  right: 16,
  zIndex: 10000,
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  maxWidth: 420,
};

const MESSAGE_STYLE: React.CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 12,
  padding: '10px 12px',
  borderRadius: 6,
  borderWidth: 1,
  borderStyle: 'solid',
  color: '#ffffff',
  fontSize: 14,
  lineHeight: 1.4,
  boxShadow: '0 2px 8px rgba(0,0,0,0.35)',
};

const DISMISS_STYLE: React.CSSProperties = {
  background: 'transparent',
  border: 'none',
  color: '#ffffff',
  cursor: 'pointer',
  fontSize: 18,
  lineHeight: 1,
  padding: 0,
};
